/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useMediaQuery } from 'hooks/useMediaQuery';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CrmDataEnrichmentPlatforms.module.scss';

type Translate = (key: string) => string;

const getCapabilities = (t: Translate) => [
  t('CRM objects'),
  t('How it installs'),
  t('Bulk backfill'),
  t('New records'),
  t('Inside automation'),
  t('Field mapping'),
];

const getPlatforms = (t: Translate) => [
  {
    name: 'HubSpot',
    sub: 'App Marketplace',
    values: [
      t('Companies'),
      t('App Marketplace, connected to your portal'),
      t('Across the existing database, filtered by list or property'),
      t('Matched and completed as they land'),
      t('Called as a workflow action, with branching on the result'),
      t('Standard or custom properties, chosen field by field'),
    ],
    ctaLabel: t('Install from the Marketplace'),
    ctaHref: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment',
    external: true,
  },
  {
    name: 'Salesforce',
    sub: 'AppExchange',
    values: [
      t('Accounts and Leads'),
      t('Managed package from AppExchange, installed by your admin'),
      t('Across a list view, on the records you select'),
      t('Matched and completed as they land'),
      t('Called as a step inside Flow, output usable downstream'),
      t('Admin-controlled, respecting field-level security'),
    ],
    ctaLabel: t('Get it on AppExchange'),
    ctaHref: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4',
    external: true,
  },
  {
    name: 'Dynamics 365',
    sub: t('Solution deployment'),
    values: [
      t('Accounts'),
      t('Solution deployed into your environment'),
      t('Across existing account records'),
      t('Matched and completed from the record form'),
      t('Scoped with your admin during deployment'),
      t('Standard or custom Dataverse columns you nominate'),
    ],
    ctaLabel: t('See the Dynamics setup'),
    ctaHref: '/microsoft-dynamics-integration',
    external: false,
  },
];

const MOBILE_QUERY_WIDTH = 1024;

export const CrmDataEnrichmentPlatforms = () => {
  const { t } = useInt();

  const isDesktop = useMediaQuery(MOBILE_QUERY_WIDTH);

  const CAPABILITIES = getCapabilities(t);
  const PLATFORMS = getPlatforms(t);

  const headRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const pullRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const revealables = [headRef.current, ...itemRefs.current, pullRef.current].filter((el): el is HTMLElement =>
      Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [isDesktop]);

  return (
    <Section className={styles.root}>
      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock align="left" title={t('It runs inside the CRM your team already has open.')} />
      </div>

      {isDesktop === false ? (
        <div className={styles.cards}>
          {PLATFORMS.map((platform, i) => (
            <div
              key={platform.name}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              className={cn(styles.card, styles.reveal)}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className={styles.cardHead}>
                <h3 className={styles.cardTitle}>{platform.name}</h3>
                <span className={styles.cardSub}>{platform.sub}</span>
              </div>

              {CAPABILITIES.map((capability, j) => (
                <div key={capability} className={styles.cardRow}>
                  <span className={styles.cardLabel}>{capability}</span>
                  <span className={styles.cardValue}>{platform.values[j]}</span>
                </div>
              ))}

              <Button
                className={styles.cardCta}
                type="dark-outline"
                size="md"
                href={platform.ctaHref}
                target={platform.external ? '_blank' : undefined}
                rel={platform.external ? 'noopener noreferrer' : undefined}
              >
                {platform.ctaLabel}
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.capHead}>{t('Capability')}</th>
                {PLATFORMS.map((platform) => (
                  <th key={platform.name}>
                    <span className={styles.platformName}>{platform.name}</span>
                    <span className={styles.platformSub}>{platform.sub}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPABILITIES.map((capability, i) => (
                <tr
                  key={capability}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  className={cn(styles.row, styles.reveal)}
                  style={{ transitionDelay: `${i * 55}ms` }}
                >
                  <td className={styles.capCell}>{capability}</td>
                  {PLATFORMS.map((platform) => (
                    <td key={platform.name} className={styles.valueCell}>
                      {platform.values[i]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={styles.ctaRow}>
                <td className={styles.capCell}>{t('Get it')}</td>
                {PLATFORMS.map((platform) => (
                  <td key={platform.name} className={styles.ctaCell}>
                    <Button
                      type="dark-outline"
                      size="sm"
                      href={platform.ctaHref}
                      target={platform.external ? '_blank' : undefined}
                      rel={platform.external ? 'noopener noreferrer' : undefined}
                    >
                      {platform.ctaLabel}
                    </Button>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div ref={pullRef} className={cn(styles.pull, styles.reveal)}>
        <span className={styles.pullMark}>“</span>
        <p className={styles.pullText}>
          {t(
            'Same matching, same field set and the same source attribution on all three. What differs is only where the enrichment is triggered from.',
          )}
        </p>
      </div>
    </Section>
  );
};
